/**
 * AppInitializer.tsx
 * 
 * Handles initial app loading of public data that doesn't require authentication
 * This includes loading all available shares/symbols from SHARES_XML
 * 
 * This component should wrap your entire app and load before anything else
 */

import React, { useEffect, useState, createContext, useContext } from 'react';
import { Quote } from './Quote';
import * as Const from '../Constants';

var XMLParser = require('react-xml-parser');

// ============================================================================
// CONTEXT FOR SHARES DATA
// ============================================================================

interface SharesContextType {
    symbols: Quote[];
    isLoading: boolean;
    error: string | null;
}

const SharesContext = createContext<SharesContextType>({
    symbols: [],
    isLoading: true,
    error: null
});

// Custom hook to use shares data
export const useShares = () => {
    const context = useContext(SharesContext);
    if (!context) {
        throw new Error('useShares must be used within SharesProvider');
    }
    return context;
};

// ============================================================================
// SHARES PROVIDER COMPONENT
// ============================================================================

interface SharesProviderProps {
    children: React.ReactNode;
}

export const SharesProvider: React.FC<SharesProviderProps> = ({ children }) => {
    const [symbols, setSymbols] = useState<Quote[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadAllShares();
    }, []);

    /**
     * Load all shares/symbols from SHARES_XML
     * This is PUBLIC data - no authentication required
     */
    const loadAllShares = () => {
        const xhr = new XMLHttpRequest();

        xhr.open("GET", Const.SHARES_XML, true);

        xhr.onreadystatechange = function () {
            if (xhr.readyState === 4) {
                if (xhr.status === 200) {
                    try {
                        const xml = new XMLParser().parseFromString(xhr.responseText);
                        const quotesData = parseQuotesData(xml);
                        setSymbols(quotesData);
                        setIsLoading(false);
                        console.log('? Loaded', quotesData.length, 'symbols');
                    } catch (err) {
                        console.error('Error parsing shares XML:', err);
                        setError('Failed to parse shares data');
                        setIsLoading(false);
                    }
                } else {
                    console.error('Failed to load shares:', xhr.statusText);
                    setError(xhr.statusText);
                    setIsLoading(false);
                }
            }
        };

        xhr.onerror = function () {
            console.error('Network error loading shares:', xhr.statusText);
            setError('Network error');
            setIsLoading(false);
        };

        xhr.send();
    };

    /**
     * Parse XML data into Quote objects
     */
    const parseQuotesData = (xml: any): Quote[] => {
        const data: Const.XmlElement = xml;
        const quotesList: Quote[] = [];

        for (let i = 0; i < data.children.length; i++) {
            const quote = new Quote();

            quote.nameEn = data.children[i].attributes["name"];
            quote.nameAr = data.children[i].attributes["name_ar"];
            quote.reuter = data.children[i].attributes["reuters"];
            quote.yearLow = data.children[i].attributes["low_year"];
            quote.yearHigh = data.children[i].attributes["high_year"];
            quote.id = data.children[i].attributes["id"];
            quote.currency = data.children[i].attributes["curr"];
            quote.code = data.children[i].attributes["code"];
            quote.sectorEn = data.children[i].attributes["sector_en"];
            quote.sectorAr = data.children[i].attributes["sector_ar"];

            const indices: string = data.children[i].attributes["indices"];
            quote.indicesList = (indices === "0") ? ["NA"] : indices.split(",");

            quote.type = data.children[i].attributes["symbol_type"];
            quote.status = data.children[i].attributes["symbol_status"];
            quote.marketCode = data.children[i].attributes["market_code"];
            quote.listedShares = data.children[i].attributes["listed_shares"];

            quote.name = data.children[i].attributes["reuters"] + " " + data.children[i].attributes["name"];
            quote.nameArabic = data.children[i].attributes["reuters"] + " " + data.children[i].attributes["name_ar"];

            quotesList.push(quote);
        }

        return quotesList;
    };

    return (
        <SharesContext.Provider value={{ symbols, isLoading, error }}>
            {children}
        </SharesContext.Provider>
    );
};

// ============================================================================
// LOADING FALLBACK COMPONENT
// ============================================================================

export const SharesLoadingFallback: React.FC = () => {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column'
        }}>
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading market data...</p>
        </div>
    );
};

// ============================================================================
// APP INITIALIZER COMPONENT
// ============================================================================

interface AppInitializerProps {
    children: React.ReactNode;
    loadingFallback?: React.ReactNode;
}

/**
 * Main AppInitializer component
 * Wraps your app and ensures shares data is loaded before rendering
 */
export const AppInitializer: React.FC<AppInitializerProps> = ({
    children,
    loadingFallback = <SharesLoadingFallback />
}) => {
    return (
        <SharesProvider>
            <SharesContext.Consumer>
                {({ isLoading, error }) => {
                    if (error) {
                        return (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',
                                flexDirection: 'column'
                            }}>
                                <div className="alert alert-danger" role="alert">
                                    <h4 className="alert-heading">Error Loading Data</h4>
                                    <p>{error}</p>
                                    <button
                                        className="btn btn-danger"
                                        onClick={() => window.location.reload()}
                                    >
                                        Retry
                                    </button>
                                </div>
                            </div>
                        );
                    }

                    if (isLoading) {
                        return <>{loadingFallback}</>;
                    }

                    return <>{children}</>;
                }}
            </SharesContext.Consumer>
        </SharesProvider>
    );
};

export default AppInitializer;

// ============================================================================
// USAGE EXAMPLE
// ============================================================================

/*

// In your index.tsx or App.tsx:

import { AppInitializer, useShares } from './components/AppInitializer';
import { AuthProvider } from './auth/AuthContext';

function App() {
  return (
    <AppInitializer>
      <AuthProvider>
        <Router>
          <YourAppContent />
        </Router>
      </AuthProvider>
    </AppInitializer>
  );
}

// In any component that needs symbols:

const MyComponent: React.FC = () => {
  const { symbols } = useShares(); // Get loaded symbols
  const { user } = useAuth();      // Get auth user
  
  return (
    <div>
      <h1>Available Symbols: {symbols.length}</h1>
      {symbols.map(symbol => (
        <div key={symbol.id}>{symbol.reuter}</div>
      ))}
    </div>
  );
};

// Or pass to platform components:

<MarketPlatform
  symbols={symbols}  // from useShares()
  custodyList={custodyList}
/>

*/